import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';
import { MINIMUM_EVENT_HEIGHT } from '../constants';
import type { CalendarEvent } from '../types';
import { useGetAllDayEventsForDay } from './useGetAllDayEventsForDay';

export const getEventInTimezone = <T extends CalendarEvent>(event: T, timezone: string): T => {
  const startDate = toZonedTime(event.start, timezone);
  const endDate = toZonedTime(event.end, timezone);

  return {
    ...event,
    end: endDate,
    start: startDate,
  };
};

type Properties<T> = {
  days: Date[];
  events?: T[];
  primaryTimezone: string;
};

export const useGetSortedEventsInTimezone = <T extends CalendarEvent>({
  days,
  events,
  primaryTimezone,
}: Properties<T>) => {
  const eventsInTimezone = useMemo(
    () => events?.map((event) => getEventInTimezone<T>(event, primaryTimezone)) ?? [],
    [events, primaryTimezone],
  );

  const getAllDayEventsForDay = useGetAllDayEventsForDay<T>({
    events: eventsInTimezone,
  });

  const { allDayEvents, maxAllDayCount, timeSlotEvents } = useMemo(() => {
    const allDayEventsInTimezone: T[] = [];
    const timeSlotEventsInTimezone: T[] = [];

    for (const event of eventsInTimezone) {
      if (event.isAllDay) {
        allDayEventsInTimezone.push(event);
      } else {
        timeSlotEventsInTimezone.push(event);
      }
    }

    let maxAllDayCountInTimezone = 0;
    for (const day of days) {
      const count = getAllDayEventsForDay({ day }).length;
      maxAllDayCountInTimezone = Math.max(maxAllDayCountInTimezone, count);
    }

    return {
      allDayEvents: allDayEventsInTimezone,
      maxAllDayCount: maxAllDayCountInTimezone,
      timeSlotEvents: timeSlotEventsInTimezone,
    };
  }, [eventsInTimezone, days, getAllDayEventsForDay]);

  return useMemo(
    () => ({
      allDayEvents,
      allDayRowHeightPx: maxAllDayCount * MINIMUM_EVENT_HEIGHT,
      timeSlotEvents,
    }),
    [allDayEvents, maxAllDayCount, timeSlotEvents],
  );
};
